import { OvFormInput } from '@components'
import { LABELS, TOOLTIPS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Alert, Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { IUser } from '@typings'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { profileValidationSchema } from './validation/profile.form.validation'

export const ProfileForm = observer(() => {
  const { user } = useStore()
  const [showEmailChange, setShowEmailChange] = useState(false)

  const form = useForm<{ name: string }>({
    resolver: yupResolver(profileValidationSchema()),
    defaultValues: async () => {
      const userData = await user.whoAmI()
      return {
        name: userData.name,
      }
    },
    mode: 'onChange',
  })

  const onSubmit = async (data: { name: string }) => {
    try {
      await user.update({ ...user.current_user, name: data.name })
      toast.success('Profiel succesvol bijgewerkt')
      form.reset(data)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Er is een fout opgetreden')
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  const FormInput = OvFormInput<{ name: string }>

  return (
    <OvFormProvider
      form={form}
      onSubmit={() => form.handleSubmit(onSubmit)()}
      onCancel={handleCancel}
      loading={user.loading}
      enableNavigationBlocker={false}>
      <Paper sx={{ p: 3 }}>
        <Stack
          direction="column"
          gap={3}>
          <Typography variant="h4">Profiel</Typography>

          <OvForm>
            <Stack
              direction="column"
              gap={2}>
              <FormInput
                label="Naam"
                name="name"
                helperText="Je volledige naam"
              />

              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom>
                  E-mailadres
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 1 }}>
                  {user.current_user?.email}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowEmailChange(!showEmailChange)}>
                  {showEmailChange ? 'Annuleer wijziging' : 'Wijzig e-mailadres'}
                </Button>
              </Box>

              {showEmailChange && (
                <Alert severity="info">
                  E-mail wijzigen functionaliteit komt binnenkort. De backend API bestaat al op{' '}
                  <code>/user/request-email-change</code>
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Tooltip title={TOOLTIPS.CANCEL_ESC}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={!form.formState.isDirty}>
                      {LABELS.CANCEL}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={TOOLTIPS.SAVE_PROFILE}>
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      type="submit"
                      disabled={!form.formState.isDirty || user.loading}>
                      {LABELS.PROFILE_SAVE}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          </OvForm>
        </Stack>
      </Paper>
    </OvFormProvider>
  )
})
